import type { ApiClient } from './client';
import { moduleApi } from './module-api';

/** An endpoint table whose every entry is an async call (what can be loaded lazily) */
export type AsyncTable<T> = { [K in keyof T]: T[K] extends (...args: never[]) => Promise<unknown> ? T[K] : never };

const preloaders = new WeakMap<object, () => Promise<void>>();

/**
 * A module's endpoint table that loads with first use (docs/ux/plan.md, B5): `api.contacts.list()`
 * keeps working everywhere, but the table's code is a separate chunk instead of part of the
 * initial bundle. Every entry must return a promise (checked by the type), so a call made
 * before the chunk arrives simply waits for it. Each entry keeps one identity, so it is safe
 * in dependency arrays. Built once per client (module-api.ts).
 */
export function lazyApi<T extends AsyncTable<T>>(client: ApiClient, load: () => Promise<(client: ApiClient) => T>): T {
  let built: T | null = null;
  let pending: Promise<T> | null = null;
  const ensure = (): Promise<T> => {
    pending ??= load().then(
      (factory) => {
        built = moduleApi(client, factory);
        return built;
      },
      (error: unknown) => {
        pending = null;
        throw new Error('This part of the app could not be loaded. Check your connection and try again.', { cause: error });
      }
    );
    return pending;
  };
  const entries = new Map<PropertyKey, (...args: unknown[]) => Promise<unknown>>();
  const call = (table: T, key: PropertyKey, args: unknown[]): Promise<unknown> => {
    const entry: unknown = (table as Record<PropertyKey, unknown>)[key];
    if (typeof entry !== 'function') return Promise.reject(new TypeError(`No endpoint ${String(key)}`));
    return (entry as (...a: unknown[]) => Promise<unknown>)(...args);
  };
  const proxy = new Proxy(
    {},
    {
      get(_target, key) {
        // Never look like a promise (await api.contacts must not try to call .then)
        if (key === 'then') return undefined;
        let entry = entries.get(key);
        if (!entry) {
          entry = (...args: unknown[]) => (built ? call(built, key, args) : ensure().then((table) => call(table, key, args)));
          entries.set(key, entry);
        }
        return entry;
      },
    }
  );
  preloaders.set(proxy, () => ensure().then(() => undefined));
  return proxy as T;
}

/** Load a lazy table's code ahead of its first call (no-op for anything else) */
export function preloadApi(table: object): Promise<void> {
  return preloaders.get(table)?.() ?? Promise.resolve();
}
